import { Component, OnDestroy, OnInit } from '@angular/core';
import * as moment from 'moment';
import { PayScalesService } from '../pay-scales/pay-scales.service';
import { IPayScaleGlobalSettings, IPilot } from '../pay-scales/pay-scales.types';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertService } from '../../core';
import { BaseComponent } from '../base.component';
import { TimeSheetService } from '../time-sheets/time-sheet.service';
import { TimeSheetStatusEnum } from '../time-sheets/time-sheet-status.enum';
import { NavigationEnd, Router } from '@angular/router';
import { PilotsService } from '../../shared/services/pilots.service';
import * as _ from 'lodash';
import { AlertDialogComponent } from '../dialogs';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

/**
 * Heaader for pilotpay
 */
@Component({
	selector: 'app-pilotpay-dashboard',
	styleUrls: ['./admin-dashboard.component.scss'],
	templateUrl: './admin-dashboard.component.html'
})
export class AdminDashboardComponent extends BaseComponent implements OnInit, OnDestroy {
	get timeSheetStatusEnum(): any {
		return TimeSheetStatusEnum;
	}

	constructor(
		protected alertService: AlertService,
		private payScalesService: PayScalesService,
		private pilotsService: PilotsService,
		private timeSheetService: TimeSheetService,
		private router: Router,
		private dialog: MatDialog
	) {
		super(alertService);
	}

	public pilots: IPilot[];
	public selectedDate: moment.Moment = moment();
	public isTimeSheetLocked: boolean;
	public isLoading: boolean;
	public payPeriods: any;
	public notStarted: number;
	public inProgress: number;
	public approvedByAdmin: number;
	public submittedByEmployee: number;
	public paid: number;
	public payrollEnd: boolean;
	public timeSheetStatus: number;
	public tsSubmissionDeadline: number;
	public timeSheetLockOutDay: number;
	public passedSubmissionDue: number;
	public isAfterDeadlineWarning: boolean;
	public selectedDateSerialized: string;
	public currentDate: string;
	public previousDate: string;
	public areAllTimeSheetsPaid: boolean;
	public dialogConfig: MatDialogConfig = new MatDialogConfig();
	public alertDialogRef: MatDialogRef<AlertDialogComponent>;
	private routerSubscription: Subscription;

	ngOnInit(): void {
		this.routerSubscription = this.router.events.subscribe(event => {
			if (event instanceof NavigationEnd && event.url.includes('admin')) {
				this.getPilots();
			}
		});
		this.getLockOutDate();

		this.currentDate = moment()
			.set('date', 1)
			.set('month', moment().get('month'))
			.startOf('day')
			.format('YYYY-MM-DDTHH:mm:ss');
		this.previousDate = moment()
			.subtract(1, 'month')
			.set('date', 1)
			.startOf('day')
			.format('YYYY-MM-DDTHH:mm:ss');
	}

	ngOnDestroy(): void {
		if (this.routerSubscription) {
			this.routerSubscription.unsubscribe();
		}
	}

	public onDateChange(date: any): void {
		this.selectedDate = moment(date);
		this.getPilots();
		this.alertService.clearErrors();
	}

	public getPilots(): void {
		this.isLoading = true;
		this.pilotsService.getPilotsWithTimeSheetData(this.selectedDateSerialized).subscribe(
			(DATA: IPilot[]) => {
				if (DATA) {
					this.pilots = DATA;
					this.areAllTimeSheetsPaid = !_.some(
						this.pilots,
						i => i.timeSheetStatus !== TimeSheetStatusEnum.Paid
					);
					const currentDate: moment.Moment = moment().utc(true);
					const lockOutDate: any = moment(this.selectedDateSerialized)
						.add(1, 'month')
						.add(this.timeSheetLockOutDay - 1, 'day')
						.utc(true);
					this.tsSubmissionDeadline = lockOutDate.diff(currentDate, 'days');
					this.isAfterDeadlineWarning = currentDate.isAfter(lockOutDate.format('YYYY-MM-DDTHH:mm:ss'));

					this.passedSubmissionDue = currentDate.diff(lockOutDate, 'days');

					this.notStarted = DATA.filter(
						x =>
							(x.timeSheetStatus === TimeSheetStatusEnum.NotStarted || x.timeSheetStatus === null) &&
							x.showInLists
					).length;
					this.approvedByAdmin = DATA.filter(
						x => x.timeSheetStatus === TimeSheetStatusEnum.ApprovedByAdmin
					).length;
					this.submittedByEmployee = DATA.filter(
						x => x.timeSheetStatus === TimeSheetStatusEnum.SubmittedByEmployee
					).length;
					this.paid = DATA.filter(x => x.timeSheetStatus === TimeSheetStatusEnum.Paid).length;
					this.inProgress = DATA.filter(x => x.timeSheetStatus === TimeSheetStatusEnum.InProgress).length;
				}
				this.isLoading = false;
			},
			(error: HttpErrorResponse) => {
				this.isLoading = false;
				this.handleError(error);
			}
		);
	}

	public exportToPayroll(pilotsList: number[]): void {
		this.dialogConfig.disableClose = false;
		this.dialogConfig.autoFocus = true;
		this.dialogConfig.data = {
			modalType: 'payroll'
		};
		if (this.dialog) {
			this.alertDialogRef = this.dialog.open(AlertDialogComponent, this.dialogConfig);
		}
		if (this.alertDialogRef) {
			this.alertDialogRef.afterClosed().subscribe((result: boolean | 'preview' | undefined) => {
				this.alertDialogRef = null;
				if (result !== true && result !== 'preview') {
					return;
				}
				this.payrollEnd = result === true;
				this.pilotsService.exportToPayroll(pilotsList, this.payrollEnd).subscribe(
					response => {
						this.downloadFile(response);
					},
					error => {
						this.handleError(error);
					}
				);
			});
		}
	}

	private downloadFile(fileData: Blob): void {
		const blob: any = new Blob([fileData], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		});

		const link: any = document.createElement('a');
		if (link.download !== undefined) {
			const url: any = URL.createObjectURL(blob);
			link.setAttribute('href', url);
			link.setAttribute('download', 'payroll');
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	}

	private getLockOutDate(): void {
		this.payScalesService.getPayScaleSetting().subscribe(
			(DATA: IPayScaleGlobalSettings) => {
				if (DATA) {
					this.timeSheetLockOutDay = DATA.timeSheetLockOutDay;
					this.isTimeSheetLocked = moment().isAfter(
						moment()
							.set('date', DATA.timeSheetLockOutDay)
							.set('month', moment().get('month'))
							.startOf('day')
					);
				}
				this.getPayPeriods();
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	private getPayPeriods(): void {
		this.timeSheetService.getPayPeriods().subscribe(
			(DATA: any) => {
				if (DATA) {
					const currentDate: string = moment()
						.set('date', 1)
						.set('month', moment().get('month'))
						.startOf('day')
						.format('YYYY-MM-DDTHH:mm:ss');
					const previousDate: string = moment()
						.subtract(1, 'month')
						.set('date', 1)
						.startOf('day')
						.format('YYYY-MM-DDTHH:mm:ss');

					this.payPeriods = DATA;

					// If a time sheet has not been created for the current month yet,
					// manually add the current month to the date dropdown
					if (!this.payPeriods.includes(currentDate)) {
						this.payPeriods.unshift(currentDate);
					}

					// Should be previous payPeriod if timeSheet is not locked yet, else current
					this.selectedDate = moment(!this.isTimeSheetLocked ? previousDate : currentDate);
					this.selectedDateSerialized = this.selectedDate.utc(true).format('YYYY-MM-DDTHH:mm:ss');
					this.getPilots();
				}
			},
			(error: HttpErrorResponse) => {
				this.handleError(error);
			}
		);
	}
}
